/** Portfolio quality & resilience tab (wheel + margin). */

import "./pfolio.css";
import { fetchAgenticSnapshot } from "../api/agenticClient";
import { remainingDte } from "../shared/marketHours";
import {
  accountScopeGeneration,
  paperAccountsEnabled,
  subscribeAccountScope,
} from "./accountScope";
import {
  activeAccountPortfolioFromSnapshot,
  buyingPowerFromActiveAccount,
  cashEquivalentFromActiveAccount,
} from "./agenticPortfolioCash";
import {
  loadOwnLedgerOpenOptions,
  loadScopedShareAvgBooks,
  type OwnLedgerShareAvgHolding,
} from "./ownLedgerClient";
import { isOwnLedgerEnabled } from "./ownLedgerFeatures";
import {
  buildPfolioSnapshot,
  dayRowFromSnapshot,
  PFOLIO_SEVERITIES,
  severityFromId,
  type PfolioDayRow,
  type PfolioHolding,
  type PfolioInputs,
  type PfolioOption,
  type PfolioSeverity,
  type PfolioSnapshot,
} from "./pfolioModel";
import { fmtUsdWhole } from "./watchlistData";
import { getApiConfig } from "../config";
import { proxyFetch } from "../api/proxyFetch";

export type { PfolioSnapshot, PfolioSeverity, PfolioDayRow };

const SEVERITY_KEY = "sfe-pfolio-severity-v1";

function esc(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

function loadSeverity(): PfolioSeverity {
  try {
    return severityFromId(localStorage.getItem(SEVERITY_KEY));
  } catch {
    return "moderate";
  }
}

function saveSeverity(id: PfolioSeverity): void {
  try {
    localStorage.setItem(SEVERITY_KEY, id);
  } catch {
    /* ignore */
  }
}

function toneClass(tone: string): string {
  if (tone === "good") return "sfe-pfolio-tone-good";
  if (tone === "watch") return "sfe-pfolio-tone-watch";
  if (tone === "risk") return "sfe-pfolio-tone-risk";
  return "sfe-pfolio-tone-neutral";
}

function bandLabel(band: PfolioSnapshot["qualityBand"]): string {
  if (band === "resilient") return "Resilient";
  if (band === "adequate") return "Adequate";
  if (band === "fragile") return "Fragile";
  return "Unknown";
}

function parseBrokerCash(portfolioRaw: unknown): number | null {
  if (!portfolioRaw || typeof portfolioRaw !== "object" || Array.isArray(portfolioRaw)) {
    return null;
  }
  const portfolio = portfolioRaw as Record<string, unknown>;
  const buyingPowerObj =
    portfolio.buying_power &&
    typeof portfolio.buying_power === "object" &&
    !Array.isArray(portfolio.buying_power)
      ? (portfolio.buying_power as Record<string, unknown>)
      : null;
  const raw = portfolio.cash ?? portfolio.cash_balance ?? buyingPowerObj?.cash;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

async function priceMapForTickers(tickers: string[]): Promise<Map<string, number>> {
  const unique = [...new Set(tickers.map((t) => t.trim().toUpperCase()).filter(Boolean))];
  if (!unique.length) return new Map();
  try {
    const { fetchAgenticEquityQuotes } = await import("../api/agenticClient");
    return await fetchAgenticEquityQuotes(unique);
  } catch {
    return new Map();
  }
}

function holdingsFromBooks(
  books: OwnLedgerShareAvgHolding[],
  prices: Map<string, number>,
): PfolioHolding[] {
  return books
    .filter((b) => b.quantity > 0 && b.avgCost > 0)
    .map((b) => {
      const price = prices.get(b.ticker) ?? null;
      const costBasis = b.costBasis > 0 ? b.costBasis : b.avgCost * b.quantity;
      return {
        ticker: b.ticker,
        accountId: b.accountId || "",
        quantity: b.quantity,
        avgCost: b.avgCost,
        costBasis,
        price,
        marketValue: price != null && price > 0 ? price * b.quantity : null,
      };
    });
}

async function loadPfolioInputs(): Promise<PfolioInputs> {
  const [books, openOpts, snapWrap] = await Promise.all([
    loadScopedShareAvgBooks(true),
    loadOwnLedgerOpenOptions(true),
    fetchAgenticSnapshot().catch(() => ({ snapshot: null })),
  ]);
  const snapshot = snapWrap?.snapshot ?? null;
  const tickers = [
    ...books.map((b) => b.ticker),
    ...openOpts.map((o) => o.ticker),
  ];
  const prices = await priceMapForTickers(tickers);
  const holdings = holdingsFromBooks(books, prices);

  const options: PfolioOption[] = openOpts.map((row) => {
    const dte = remainingDte(row.expDate);
    return {
      ticker: row.ticker,
      side: row.side,
      strike: row.strike,
      contracts: row.contracts,
      expDate: row.expDate,
      dte: dte ?? null,
      openCredit: Number(row.openCredit) || 0,
      entryPrice: Number(row.entryPrice) || 0,
      stockPrice: prices.get(row.ticker) ?? null,
      accountId: row.accountId ?? null,
    };
  });

  const activePortfolio = snapshot ? activeAccountPortfolioFromSnapshot(snapshot) : null;

  // Prefer live RH figures when not in paper-only scope.
  const paperOnly = paperAccountsEnabled();
  const cashEquivalent = paperOnly ? null : cashEquivalentFromActiveAccount(snapshot);
  const buyingPower = paperOnly ? null : buyingPowerFromActiveAccount(snapshot);
  const brokerCash = paperOnly ? null : parseBrokerCash(activePortfolio);

  // Own capital / margin cost — best-effort from /own-capital.
  let ownCapital: number | null = null;
  let monthlyMarginCost: number | null = null;
  try {
    if (!paperOnly) {
      const { baseUrl } = getApiConfig();
      if (baseUrl) {
        const url =
          baseUrl === window.location.origin
            ? "/own-capital"
            : `${baseUrl.replace(/\/$/, "")}/own-capital`;
        const res = await proxyFetch(url, {
          headers: { Accept: "application/json" },
          credentials: "include",
          cache: "no-store",
        });
        if (res.ok) {
          const data = (await res.json()) as {
            months?: Record<string, { marginCost?: number }>;
          };
          const months = data.months ?? {};
          const keys = Object.keys(months).sort();
          const current = new Date().toISOString().slice(0, 7);
          const key = keys.includes(current) ? current : keys.at(-1);
          if (key) {
            const entry = months[key];
            const mc = Number(entry?.marginCost);
            if (Number.isFinite(mc) && mc > 0) monthlyMarginCost = mc;
          }
        }
      }
    }
  } catch {
    /* optional */
  }

  const stockCostBasis = holdings.reduce((s, h) => s + h.costBasis, 0);
  if (brokerCash != null && brokerCash < 0) {
    ownCapital = Math.max(0, stockCostBasis - Math.abs(brokerCash));
  }

  return {
    holdings,
    options,
    cashEquivalent,
    buyingPower,
    brokerCash,
    ownCapital,
    monthlyMarginCost,
    asOf: new Date().toISOString(),
  };
}

export function renderPfolioHtml(
  snapshot: PfolioSnapshot,
  severity: PfolioSeverity,
  historyDays: PfolioDayRow[] = [],
  compareDate: string | null = null,
): string {
  const sevChips = PFOLIO_SEVERITIES.map((s) => {
    const active = s.id === severity ? " active" : "";
    return `<button type="button" class="year-chip sfe-pfolio-sev-chip${active}" data-pfolio-severity="${esc(s.id)}" aria-pressed="${s.id === severity ? "true" : "false"}">${esc(s.label)}</button>`;
  }).join("");

  const score =
    snapshot.qualityScore == null ? "—" : String(snapshot.qualityScore);
  const band = bandLabel(snapshot.qualityBand);

  const metricCards = snapshot.metrics
    .map(
      (m) => `<article class="sfe-pfolio-metric ${toneClass(m.tone)}">
      <span class="sfe-pfolio-k">${esc(m.label)}<span class="hint" tabindex="0" role="img" data-tip="${esc(m.tip)}">i</span></span>
      <strong class="sfe-pfolio-v">${esc(m.value)}</strong>
      <span class="sfe-pfolio-d">${esc(m.detail)}</span>
    </article>`,
    )
    .join("");

  const book = snapshot.book;
  const bookRow = [
    ["Stock basis", fmtUsdWhole(book.stockCostBasis)],
    ["Market value", fmtUsdWhole(book.stockMarketValue)],
    ["Open stock P/L", fmtUsdWhole(book.stockOpenPl)],
    ["Own capital", book.ownCapital == null ? "—" : fmtUsdWhole(book.ownCapital)],
    ["Margin used", fmtUsdWhole(book.marginUsed)],
    ["Cash equivalent", book.cashEquivalent == null ? "—" : fmtUsdWhole(book.cashEquivalent)],
    ["Put CSP", fmtUsdWhole(book.putCollateral)],
    ["Short puts", String(book.shortPutContracts)],
    ["Short calls", String(book.shortCallContracts)],
  ]
    .map(
      ([k, v]) =>
        `<div class="sfe-pfolio-book-cell"><span>${esc(k)}</span><strong>${esc(String(v))}</strong></div>`,
    )
    .join("");

  const concRows = snapshot.concentration.length
    ? snapshot.concentration
        .map(
          (r) => `<tr>
        <td>${esc(r.ticker)}</td>
        <td class="num">${esc(fmtUsdWhole(r.costBasis))}</td>
        <td class="num">${r.marketValue == null ? "—" : esc(fmtUsdWhole(r.marketValue))}</td>
        <td class="num">${esc(r.sharePct.toFixed(1))}%</td>
        <td class="num">${esc(r.quantity.toLocaleString("en-US"))}</td>
      </tr>`,
        )
        .join("")
    : `<tr><td colspan="5" class="muted">No open stock lots in scope.</td></tr>`;

  const dteRows = snapshot.dteBuckets.length
    ? snapshot.dteBuckets
        .map(
          (b) => `<tr>
        <td>${esc(b.label)}</td>
        <td class="num">${b.puts}</td>
        <td class="num">${b.calls}</td>
        <td class="num">${esc(fmtUsdWhole(b.putNotional))}</td>
      </tr>`,
        )
        .join("")
    : `<tr><td colspan="4" class="muted">No open short options in scope.</td></tr>`;

  const scenarios = snapshot.scenarios
    .map((sc) => {
      const figs = sc.figures
        .map(
          (f) =>
            `<div class="sfe-pfolio-fig ${toneClass(f.tone ?? "neutral")}"><span>${esc(f.label)}</span><strong>${esc(f.value)}</strong></div>`,
        )
        .join("");
      const lines = sc.consequences.map((c) => `<li>${esc(c)}</li>`).join("");
      return `<article class="sfe-pfolio-scenario ${toneClass(sc.tone)}">
        <header>
          <h4>${esc(sc.name)}</h4>
          <p>${esc(sc.summary)}</p>
        </header>
        <div class="sfe-pfolio-figs">${figs}</div>
        <ul class="sfe-pfolio-consequences">${lines}</ul>
      </article>`;
    })
    .join("");

  const footnotes = snapshot.footnotes
    .map((n) => `<p class="sfe-pfolio-foot">${esc(n)}</p>`)
    .join("");

  const asOf = snapshot.asOf
    ? new Date(snapshot.asOf).toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      })
    : "";

  const todayLive = dayRowFromSnapshot(snapshot);
  const historyHtml = renderHistorySection(historyDays, todayLive, compareDate);

  return `<div class="sfe-pfolio" data-pfolio-root>
    <header class="sfe-pfolio-head">
      <div class="sfe-pfolio-head-text">
        <h2 class="insight-h">Pfolio</h2>
        <p class="muted small">Wheel + margin quality, concentration, and stress sketches for the accounts in scope.${asOf ? ` Updated ${esc(asOf)}.` : ""}</p>
      </div>
      <div class="sfe-pfolio-score ${toneClass(
        snapshot.qualityBand === "resilient"
          ? "good"
          : snapshot.qualityBand === "adequate"
            ? "watch"
            : snapshot.qualityBand === "fragile"
              ? "risk"
              : "neutral",
      )}">
        <span class="sfe-pfolio-k">Resilience</span>
        <strong class="sfe-pfolio-v">${esc(score)}<span class="sfe-pfolio-score-unit">/100</span></strong>
        <span class="sfe-pfolio-d">${esc(band)}</span>
      </div>
    </header>

    <section class="sfe-pfolio-guidance">
      <article class="sfe-pfolio-guidance-card">
        <h3>Summary</h3>
        <p>${esc(snapshot.summary)}</p>
      </article>
      <article class="sfe-pfolio-guidance-card sfe-pfolio-rec">
        <h3>Recommendation</h3>
        <p>${esc(snapshot.recommendation)}</p>
      </article>
    </section>

    <section class="sfe-pfolio-metrics">${metricCards}</section>

    ${historyHtml}

    <section class="sfe-pfolio-book">
      <h3 class="insight-h">Book snapshot</h3>
      <div class="sfe-pfolio-book-grid">${bookRow}</div>
    </section>

    <div class="sfe-pfolio-split">
      <section class="sfe-pfolio-table-card">
        <h3 class="insight-h">Concentration</h3>
        <div class="sfe-pfolio-table-wrap">
          <table class="sfe-pfolio-table">
            <thead><tr><th>Ticker</th><th class="num">Basis</th><th class="num">Mkt</th><th class="num">Weight</th><th class="num">Shares</th></tr></thead>
            <tbody>${concRows}</tbody>
          </table>
        </div>
      </section>
      <section class="sfe-pfolio-table-card">
        <h3 class="insight-h">Option DTE</h3>
        <div class="sfe-pfolio-table-wrap">
          <table class="sfe-pfolio-table">
            <thead><tr><th>Bucket</th><th class="num">Puts</th><th class="num">Calls</th><th class="num">Put CSP</th></tr></thead>
            <tbody>${dteRows}</tbody>
          </table>
        </div>
      </section>
    </div>

    <section class="sfe-pfolio-stress">
      <div class="sfe-pfolio-stress-head">
        <div>
          <h3 class="insight-h">Market event simulation</h3>
          <p class="muted small">Pick a severity. Each card is a separate sketch of what that shock could do to this book.</p>
        </div>
        <div class="sfe-pfolio-sev" role="tablist" aria-label="Stress severity">${sevChips}</div>
      </div>
      <div class="sfe-pfolio-scenarios">${scenarios}</div>
    </section>

    ${footnotes}
  </div>`;
}

function fmtPct(n: number | null | undefined, digits = 0): string {
  if (n == null || !Number.isFinite(n)) return "—";
  return `${n.toFixed(digits)}%`;
}

function fmtMo(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return "—";
  return `${n.toFixed(n >= 10 ? 0 : 1)} mo`;
}

function deltaClass(delta: number | null, higherIsBetter: boolean): string {
  if (delta == null || !Number.isFinite(delta) || Math.abs(delta) < 0.05) {
    return "sfe-pfolio-delta-flat";
  }
  const up = delta > 0;
  const good = higherIsBetter ? up : !up;
  return good ? "sfe-pfolio-delta-up" : "sfe-pfolio-delta-down";
}

function dayLabel(date: string): string {
  const [y, m, d] = date.split("-").map(Number);
  if (!y || !m || !d) return date;
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
}

function renderHistorySection(
  historyDays: PfolioDayRow[],
  todayLive: PfolioDayRow,
  compareDate: string | null,
): string {
  const byDate = new Map(historyDays.map((d) => [d.date, d]));
  const stripDays = [...historyDays].slice(-30);
  if (!stripDays.length) {
    return `<section class="sfe-pfolio-history">
      <h3 class="insight-h">Daily history</h3>
      <p class="sfe-pfolio-history-note">History starts today. A compact row is saved automatically after each Robinhood snapshot refresh, and again when you open Pfolio.</p>
    </section>`;
  }

  const selected =
    (compareDate && byDate.get(compareDate)) ||
    stripDays[stripDays.length - 1] ||
    null;
  const baseline = todayLive;
  const chips = stripDays
    .map((d) => {
      const active = selected && d.date === selected.date ? " active" : "";
      const sc = d.qualityScore == null ? "—" : String(d.qualityScore);
      return `<button type="button" class="sfe-pfolio-day-chip${active}" data-pfolio-day="${esc(d.date)}" aria-pressed="${active ? "true" : "false"}">
        <span class="sfe-pfolio-day-lab">${esc(dayLabel(d.date))}</span>
        <span class="sfe-pfolio-day-score">${esc(sc)}</span>
      </button>`;
    })
    .join("");

  const rows: Array<{
    label: string;
    now: string;
    then: string;
    delta: string;
    cls: string;
  }> = [];

  const push = (
    label: string,
    nowVal: number | null | undefined,
    thenVal: number | null | undefined,
    kind: "pct" | "usd" | "mo" | "score",
    higherIsBetter: boolean,
    formatNow: (n: number | null | undefined) => string,
  ) => {
    const delta = nowVal != null && thenVal != null ? nowVal - thenVal : null;
    let deltaText = "—";
    if (delta != null) {
      if (kind === "usd") deltaText = `${delta >= 0 ? "+" : "−"}${fmtUsdWhole(Math.abs(delta))}`;
      else if (kind === "pct") deltaText = `${delta >= 0 ? "+" : ""}${delta.toFixed(1)} pp`;
      else if (kind === "mo") deltaText = `${delta >= 0 ? "+" : ""}${delta.toFixed(1)} mo`;
      else deltaText = `${delta >= 0 ? "+" : ""}${Math.round(delta)}`;
    }
    rows.push({
      label,
      now: formatNow(nowVal),
      then: formatNow(thenVal),
      delta: deltaText,
      cls: deltaClass(delta, higherIsBetter),
    });
  };

  if (selected) {
    push(
      "Resilience",
      baseline.qualityScore,
      selected.qualityScore,
      "score",
      true,
      (n) => (n == null ? "—" : String(Math.round(n))),
    );
    push(
      "Margin util",
      baseline.marginUtilizationPct,
      selected.marginUtilizationPct,
      "pct",
      false,
      (n) => fmtPct(n, 0),
    );
    push(
      "Cash vs CSP",
      baseline.collateralCoveragePct,
      selected.collateralCoveragePct,
      "pct",
      true,
      (n) => fmtPct(n, 0),
    );
    push(
      "Top weight",
      baseline.topTickerPct,
      selected.topTickerPct,
      "pct",
      false,
      (n) => fmtPct(n, 0),
    );
    push(
      "Puts ≤21 DTE",
      baseline.nearPutNotional,
      selected.nearPutNotional,
      "usd",
      false,
      (n) => (n == null ? "—" : fmtUsdWhole(n)),
    );
    push(
      "Margin used",
      baseline.marginUsed,
      selected.marginUsed,
      "usd",
      false,
      (n) => (n == null ? "—" : fmtUsdWhole(n)),
    );
    push(
      "Runway",
      baseline.runwayMonths,
      selected.runwayMonths,
      "mo",
      true,
      (n) => fmtMo(n),
    );
  }

  const compareTable = selected
    ? `<div class="sfe-pfolio-compare">
      <table>
        <thead><tr><th>Metric</th><th class="num">Now</th><th class="num">${esc(dayLabel(selected.date))}</th><th class="num">Δ</th></tr></thead>
        <tbody>
          ${rows
            .map(
              (r) => `<tr>
            <td>${esc(r.label)}</td>
            <td class="num">${esc(r.now)}</td>
            <td class="num">${esc(r.then)}</td>
            <td class="num ${r.cls}">${esc(r.delta)}</td>
          </tr>`,
            )
            .join("")}
        </tbody>
      </table>
    </div>
    <p class="sfe-pfolio-history-note">${esc(selected.summary || "")}${selected.recommendation ? ` ${esc(selected.recommendation)}` : ""}</p>`
    : "";

  return `<section class="sfe-pfolio-history">
    <h3 class="insight-h">Daily history</h3>
    <p class="sfe-pfolio-history-note">Tap a day to compare against now. Rows save automatically after Robinhood snapshot refresh.</p>
    <div class="sfe-pfolio-day-strip" role="list">${chips}</div>
    ${compareTable}
  </section>`;
}

async function pfolioApiUrl(path: string): Promise<string> {
  const { baseUrl } = getApiConfig();
  if (!baseUrl || baseUrl === window.location.origin) return path;
  return `${baseUrl.replace(/\/$/, "")}${path}`;
}

export async function fetchPfolioHistory(limit = 90): Promise<PfolioDayRow[]> {
  try {
    const url = await pfolioApiUrl(`/pfolio/history?limit=${limit}`);
    const res = await proxyFetch(url, {
      headers: { Accept: "application/json" },
      credentials: "include",
      cache: "no-store",
    });
    if (!res.ok) return [];
    const data = (await res.json()) as { days?: PfolioDayRow[] };
    return Array.isArray(data.days) ? data.days : [];
  } catch {
    return [];
  }
}

export async function persistPfolioDay(snapshot: PfolioSnapshot): Promise<PfolioDayRow | null> {
  try {
    const url = await pfolioApiUrl("/pfolio/history");
    const body = dayRowFromSnapshot(snapshot);
    const res = await proxyFetch(url, {
      method: "POST",
      headers: { Accept: "application/json", "Content-Type": "application/json" },
      credentials: "include",
      cache: "no-store",
      body: JSON.stringify(body),
    });
    if (!res.ok) return null;
    const data = (await res.json()) as { day?: PfolioDayRow };
    return data.day ?? null;
  } catch {
    return null;
  }
}

export async function fetchPfolioSnapshot(
  severity: PfolioSeverity = loadSeverity(),
): Promise<PfolioSnapshot> {
  const inputs = await loadPfolioInputs();
  return buildPfolioSnapshot(inputs, severity);
}

export class PfolioController {
  private panel: HTMLElement | null = null;
  private severity: PfolioSeverity = loadSeverity();
  private unsubScope: (() => void) | null = null;
  private scopeGen = accountScopeGeneration();
  private bound = false;
  private historyDays: PfolioDayRow[] = [];
  private compareDate: string | null = null;
  private snapshot: PfolioSnapshot | null = null;

  mount(panel: HTMLElement): void {
    this.panel = panel;
    this.severity = loadSeverity();
    panel.innerHTML = `<div class="sfe-pfolio"><p class="muted small">Loading portfolio…</p></div>`;
    if (!this.bound) {
      this.bound = true;
      panel.addEventListener("click", (event) => {
        const target = event.target;
        if (!(target instanceof Element)) return;
        const dayBtn = target.closest<HTMLButtonElement>("[data-pfolio-day]");
        if (dayBtn && this.panel?.contains(dayBtn)) {
          const day = dayBtn.getAttribute("data-pfolio-day");
          if (day && day !== this.compareDate) {
            this.compareDate = day;
            this.paint();
          }
          return;
        }
        const btn = target.closest<HTMLButtonElement>("[data-pfolio-severity]");
        if (!btn || !this.panel?.contains(btn)) return;
        const next = severityFromId(btn.getAttribute("data-pfolio-severity"));
        if (next === this.severity) return;
        this.severity = next;
        saveSeverity(next);
        void this.refresh(true);
      });
    }
    this.unsubScope?.();
    this.unsubScope = subscribeAccountScope(() => {
      const gen = accountScopeGeneration();
      if (gen === this.scopeGen) return;
      this.scopeGen = gen;
      if (this.panel?.isConnected) void this.refresh(true);
    });
  }

  private paint(): void {
    if (!this.panel || !this.snapshot) return;
    this.panel.innerHTML = renderPfolioHtml(
      this.snapshot,
      this.severity,
      this.historyDays,
      this.compareDate,
    );
  }

  async refresh(_force = false): Promise<void> {
    if (!this.panel) return;
    if (!isOwnLedgerEnabled()) {
      this.panel.innerHTML = `<div class="sfe-pfolio"><p class="muted small">Pfolio needs the own ledger enabled for your account.</p></div>`;
      return;
    }
    try {
      const snapshot = await fetchPfolioSnapshot(this.severity);
      this.snapshot = snapshot;
      void persistPfolioDay(snapshot).then(async () => {
        this.historyDays = await fetchPfolioHistory(90);
        if (!this.compareDate && this.historyDays.length) {
          this.compareDate = this.historyDays[this.historyDays.length - 1]?.date ?? null;
        }
        this.paint();
      });
      this.historyDays = await fetchPfolioHistory(90);
      if (!this.compareDate && this.historyDays.length) {
        this.compareDate = this.historyDays[this.historyDays.length - 1]?.date ?? null;
      }
      this.paint();
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Could not load Pfolio.";
      this.panel.innerHTML = `<div class="sfe-pfolio"><p class="muted small">${esc(msg)}</p></div>`;
    }
  }
}
